<template>
    <div class="summary-container">
        <h4 class="summary-heading">Summary</h4>
        <!-- Unified Summary Card -->
        <div class="summary-card-unified">
            <div class="card-content-unified">
                <!-- Image Section -->
                <div class="image-section">
                    <div class="image-wrapper">
                        <el-image
                            class="animal-image"
                            :src="animalImageUrl"
                            :preview-src-list="animalImages"
                            fit="cover">
                        </el-image>
                        <!-- Note Section -->
                        <div class="note-section-image" v-if="summary.note">
                            <span class="note-label-small">Note</span>
                            <p class="note-text-small">{{ summary.note }}</p>
                        </div>
                    </div>
                </div>

                <!-- Details Section -->
                <div class="details-section">
                    <!-- Status Section -->
                    <div class="info-section">
                        <div class="section-header">
                            <h4 class="section-title">Status</h4>
                            <button v-if="!isReadOnly" class="modern-btn btn-primary btn-small" @click="stageAndOutcomeChangeDialogVisible = true">
                                <span class="btn-text">Change Status</span>
                            </button>
                        </div>
                        <div class="status-grid">
                            <div class="info-item">
                                <span class="info-label">Stage</span>
                                <span class="info-value">{{ summary.stage || 'N/A' }}</span>
                            </div>
                            <div class="info-item">
                                <span class="info-label">Sterilization Status</span>
                                <span class="info-value">{{ summary.sterilizationStatus || 'N/A' }}</span>
                            </div>
                            <div class="info-item">
                                <span class="info-label">Outcome</span>
                                <span class="info-value">{{ summary.outcome || 'N/A' }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- Basic Information -->
                    <div class="info-section">
                        <div class="section-header">
                            <h4 class="section-title">Basic Information</h4>
                            <el-popover
                                v-if="!isReadOnly"
                                placement="left"
                                popper-class="edit-detail-popover"
                                width="520"
                                trigger="click">
                                <div class="edit-dialog">
                                    <div class="dialog-header">
                                        <h4>Edit Details</h4>
                                    </div>
                                    <div class="dialog-content">
                                        <div class="form-grid">
                                            <div class="form-group">
                                                <label>Animal Name</label>
                                                <el-input
                                                    v-model="summary.newAnimalName"
                                                    size="small"
                                                    placeholder="Enter name"></el-input>
                                            </div>
                                            <div class="form-group">
                                                <label>Gender</label>
                                                <el-select v-model="summary.newGender" size="small" placeholder="Select">
                                                    <el-option
                                                        v-for="item in list.animalGenders"
                                                        :key="item.value"
                                                        :label="item.label"
                                                        :value="item.value">
                                                    </el-option>
                                                </el-select>
                                            </div>
                                            <div class="form-group">
                                                <label>Temporary Collar ID</label>
                                                <el-input
                                                    v-model="summary.newTempCollarId"
                                                    size="small"
                                                    placeholder="Enter ID"></el-input>
                                            </div>
                                            <div class="form-group">
                                                <label>Species</label>
                                                <el-select v-model="summary.species" size="small" placeholder="Select">
                                                    <el-option
                                                        v-for="item in list.species"
                                                        :key="item.animal.species_name"
                                                        :label="item.animal.species_name"
                                                        :value="item.animal.species_name">
                                                    </el-option>
                                                </el-select>
                                            </div>
                                            <div class="form-group">
                                                <label>Colour</label>
                                                <el-select v-model="summary.newColour" size="small" placeholder="Select">
                                                    <el-option
                                                        v-for="item in list.animalColors"
                                                        :key="item.value"
                                                        :label="item.label"
                                                        :value="item.value">
                                                    </el-option>
                                                </el-select>
                                            </div>
                                            <div class="form-group form-group-full">
                                                <label>Note</label>
                                                <el-input
                                                    v-model="summary.newNote"
                                                    type="textarea"
                                                    size="small"
                                                    placeholder="Enter notes..."
                                                    :rows="3"></el-input>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="dialog-footer">
                                        <button @click="saveRecordSummaryDetails" class="save-btn">Save Changes</button>
                                    </div>
                                </div>
                                <el-button class="modern-btn btn-secondary btn-small" slot="reference">
                                    <span class="btn-text">Edit Details</span>
                                </el-button>
                            </el-popover>
                        </div>
                        <div class="info-grid">
                            <div class="info-item">
                                <span class="info-label">Record ID</span>
                                <span class="info-value">{{ recordId || 'N/A' }}</span>
                            </div>
                            <div class="info-item">
                                <span class="info-label">App ID</span>
                                <span class="info-value">{{ summary.hsappsId || 'N/A' }}</span>
                            </div>
                            <div class="info-item">
                                <span class="info-label">RFID</span>
                                <span class="info-value">{{ summary.rfid || 'N/A' }}</span>
                            </div>
                            <div class="info-item">
                                <span class="info-label">Temporary Collar ID</span>
                                <span class="info-value">{{ summary.collarId || 'N/A' }}</span>
                            </div>
                            <div class="info-item">
                                <span class="info-label">Species</span>
                                <span class="info-value">{{ summary.species || 'N/A' }}</span>
                            </div>
                            <div class="info-item">
                                <span class="info-label">Animal Name</span>
                                <span class="info-value">{{ summary.animalName || 'N/A' }}</span>
                            </div>
                            <div class="info-item">
                                <span class="info-label">Gender</span>
                                <span class="info-value">{{ summary.gender || 'N/A' }}</span>
                            </div>
                            <div class="info-item">
                                <span class="info-label">Colour</span>
                                <span class="info-value">{{ summary.colour || 'N/A' }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <el-dialog
            title="Are you sure?"
            :visible.sync="stageChangeDialogVisible"
            width="50%"
            :before-close="handleClose">
            <span>Stage should be set automatically, changing it here manually may have unknown side effects.</span>
            <br>
            <br>
            <span>
                <label>Change Stage to:</label>
                <br>
                <el-select v-model="newStageSet" placeholder="Stage">
                    <el-option
                        v-for="item in stages"
                        :key="item.value"
                        :label="item.label"
                        :value="item.value">
                    </el-option>
                </el-select>
                <br>
                <br>
                <template v-if="newStageSet=='Cleared For Release'">
                    <label>Select Release Date</label>
                    <el-date-picker
                        v-model="releaseDate"
                        type="date"
                        format="dd/MM/yyyy"
                        value-format="yyyy-MM-dd HH:mm:ss"
                        placeholder="Select date and time">
                    </el-date-picker>
                    <br>
                    <br>
                </template>

                <br>
            </span>

            <span slot="footer" class="dialog-footer">
                <el-button @click="stageChangeDialogVisible = false">Cancel</el-button>
                <el-button type="primary" @click="changeRecordStage">Change Stage</el-button>
            </span>
        </el-dialog>

        <el-dialog
            title="Are you sure?"
            :visible.sync="outcomeChangeDialogVisible"
            width="50%"
            :before-close="handleClose">
            <span>Outcome should be set automatically, changing it here manually may have unknown side effects.</span>
            <br>
            <br>
            <span>
                <label>Change Outcome to</label>
                <br>
                <el-select v-model="newOutcomeSet" placeholder="Outcome">
                    <el-option
                        v-for="item in outcomes"
                        :key="item.value"
                        :label="item.label"
                        :value="item.value">
                    </el-option>
                </el-select>
                <br>
                <br>
            </span>

            <span slot="footer" class="dialog-footer">
                <el-button @click="outcomeChangeDialogVisible = false">Cancel</el-button>
                <el-button type="primary" @click="changeRecordOutcome">Change Outcome</el-button>
            </span>
        </el-dialog>

        <el-dialog
            title="Are you sure?"
            :visible.sync="stageAndOutcomeChangeDialogVisible"
            width="50%"
            :before-close="handleClose">
            <span>Stage and Outcome should be set automatically, changing it here manually may have unknown side effects.</span>
            <br>
            <br>
            <span>Stage is currently set to: {{ summary.stage }}</span>
            <br>
            <br>
            <label>Change Stage to</label>
            <br>
            <el-select v-model="newStageSet" placeholder="Stage">
                <el-option
                    v-for="item in stages"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value">
                </el-option>
            </el-select>
            <br>
            <br>

            <template v-if="newStageSet=='Cleared For Release'">
                <label>Select Release Date</label>
                <el-date-picker
                    v-model="releaseDate"
                    type="date"
                    format="dd/MM/yyyy"
                    value-format="yyyy-MM-dd HH:mm:ss"
                    placeholder="Select date and time">
                </el-date-picker>
                <br>
                <br>
            </template>
            <br>
            <span>Outcome is currently set to: {{ summary.outcome }}</span>
            <br>
            <br>
            <div class="row">
                    <div class="col-4">
                        <label>Change Outcome to</label>
                        <br>
                        <el-select v-model="newOutcomeSet" placeholder="Outcome" class="mr-2">
                            <el-option
                                v-for="item in outcomes"
                                :key="item.value"
                                :label="item.label"
                                :value="item.value">
                            </el-option>
                        </el-select>
                    </div>
                    <div class="col-6" v-if="['Died Pre Op', 'Died Post Op'].includes(newOutcomeSet)">
                        <div class="row">
                            <div class="col-6">
                                <label>Death Cause</label>
                                <br>
                                <el-select
                                    v-model="death_cause"
                                    placeholder="Death Cause"
                                    class="mr-2">
                                    <el-option
                                        v-for="item in deathCauses"
                                        :key="item.value"
                                        :label="item.label"
                                        :value="item.value">
                                    </el-option>
                                </el-select>
                            </div>
                            <div class="col-6" v-if="death_cause === 'other'">
                                <label>Other Death Cause</label>
                                <br>
                                <el-input
                                    v-model="death_cause_other"
                                    type="text">
                                </el-input>
                            </div>
                        </div>
                    </div>
                </div>
            <span slot="footer" class="dialog-footer">
                <el-button @click="stageAndOutcomeChangeDialogVisible = false">Cancel</el-button>
                <el-button type="primary" @click="changeRecordStageAndOutcome">Save</el-button>
            </span>
        </el-dialog>

    </div>

</template>
<script>

import {Image, Popover, Button, Input, Dialog, Select, Option, DatePicker} from 'element-ui';

export default {

    components: {
        [Image.name]: Image,
        [Button.name]: Button,
        [Popover.name]: Popover,
        [Input.name]: Input,
        [Dialog.name]: Dialog,
        [Select.name]: Select,
        [Option.name]: Option,
        [DatePicker.name]: DatePicker,
    },
    props: {
        recordId: {
            type: String,
        }
    },
    data() {
        return {

            isReadOnly: false,

            isFemaleRecord: false,
            stillInCare: true,

            list: {

                animalGenders:[
                    {
                        'label': 'Male',
                        'value': 'Male',
                    },
                    {
                        'label': 'Female',
                        'value': 'Female',
                    },
                ],

                animalColors: [
                    {
                        'label': 'Black',
                        'value': 'Black',
                    },
                    {
                        'label': 'Black/ tan',
                        'value': 'Black/ tan',
                    },
                    {
                        'label': 'Black /white',
                        'value': 'Black /white',
                    },
                    {
                        'label': 'Brindle',
                        'value': 'Brindle',
                    },
                    {
                        'label': 'Brown',
                        'value': 'Brown',
                    },
                    {
                        'label': 'Brown /white',
                        'value': 'Brown /white',
                    },
                    {
                        'label': 'Sable',
                        'value': 'Sable',
                    },
                    {
                        'label': 'White',
                        'value': 'White',
                    },
                    {
                        'label': 'White mix',
                        'value': 'White mix',
                    },

                ],

                species: []

            },

            summary: {
                hsappsId: '',
                rfid: '',
                collarId: '',
                species: '',
                animalName: '',
                gender: '',
                lastLocation: '',
                stage: '',
                sterilizationStatus: '',
                outcome: '',
                note: '',
                colour: '',
                newNote: '',
                newAnimalName: '',
                newColour: '',
                newTempCollarId: '',
                newGender:''
            },

            stageAndOutcomeChangeDialogVisible: false,

            stageChangeDialogVisible: false,

            newStageSet: 'Awaiting Exam',
            releaseDate: '',

            outcomeChangeDialogVisible: false,

            newOutcomeSet: '',

            stages: [
                {
                    label: 'Awaiting Exam',
                    value: 'Awaiting Exam',
                },
                {
                    label: 'Cleared For Sterilization',
                    value: 'Cleared For Sterilization',
                },
                {
                    label: 'Pre-Op Hold',
                    value: 'Pre-Op Hold',
                },
                {
                    label: 'Sterilized',
                    value: 'Sterilized',
                },
                {
                    label: 'Post-Op Hold',
                    value: 'Post-Op Hold',
                },
                {
                    label: 'Cleared For Release',
                    value: 'Cleared For Release',
                },
                {
                    label: 'Released/Out-of-care',
                    value: 'Released/Out-of-care',
                },
            ],

            outcomes: [
                {
                    label: 'Still In Care',
                    value: 'Still In Care',
                },
                {
                    label: 'Released In Field',
                    value: 'Released In Field',
                },
                {
                    label: 'Returned To Owner',
                    value: 'Returned To Owner',
                },
                {
                    label: 'Returned To Home',
                    value: 'Returned To Home',
                },
                {
                    label: 'Died Pre Op',
                    value: 'Died Pre Op',
                },
                {
                    label: 'Died Post Op',
                    value: 'Died Post Op',
                },
                {
                    label: 'Claimed By Owner',
                    value: 'Claimed By Owner',
                },
                {
                    label: 'Escaped',
                    value: 'Escaped',
                },
                {
                    label: 'Adopted',
                    value: 'Adopted',
                },
                {
                    label: 'Euthanasia',
                    value: 'Euthanasia',
                },
            ],

            animalImageUrl: '',
            animalImages: [],

            deathCauses: [{lable: 'Cardiac Shock', value: 'Cardiac Shock'},
                {lable: 'Heat Stroke', value: 'Heat Stroke'},
                {lable: 'Hypothermia', value: 'Hypothermia'},
                {lable: 'Internal Bleeding', value: 'Internal Bleeding'},
                {lable: 'Other (for cases where rabies is suspected)', value: 'other'}],
            death_cause: '',
            death_cause_other: '',
        }
    },
    methods: {

        handleClose() {
            this.stageAndOutcomeChangeDialogVisible = false
            this.stageChangeDialogVisible = false
            this.outcomeChangeDialogVisible = false
        },
        changeRecordStage() {
            let vm = this;

            if (vm.newStageSet == 'Cleared For Release') {
                if (vm.releaseDate == '') {
                    alert("Please set release date")
                    return;
                }
            }

            vm.stageChangeDialogVisible = false;

            axios.post('catch_release/record/change/stage', {
                record_id: vm.recordId,
                new_stage: vm.newStageSet,
                release_date: vm.releaseDate
            }).then(response => {
                if (response.data.response.error.error_code === 0) {
                    vm.summary.stage = response.data.response.data.new_stage
                    vm.newStageSet = vm.summary.stage
                    vm.showAlert('Success', response.data.response.error.msg, true);
                } else {
                    vm.showAlert('Error', response.data.response.error.error_msg, false);
                }
            }).catch(error => {
                vm.showAlert('Error', error.response.data.response.error.error_msg, false);
            })
        },

        changeRecordOutcome() {
            let vm = this;

            vm.outcomeChangeDialogVisible = false;

            axios.post('catch_release/record/change/outcome', {
                record_id: vm.recordId,
                new_outcome: vm.newOutcomeSet,
                release_date: vm.releaseDate,
                death_cause: vm.death_cause,
                death_cause_other: vm.death_cause_other
            }).then(response => {
                if (response.data.response.error.error_code === 0) {
                    vm.summary.outcome = response.data.response.data.new_outcome
                    vm.newOutcomeSet = vm.summary.outcome
                    vm.showAlert('Success', response.data.response.error.msg, true);
                } else {
                    vm.showAlert('Error', response.data.response.error.error_msg, false);
                }
            }).catch(error => {
                vm.showAlert('Error', error.response.data.response.error.error_msg, false);
            })
        },

        getRecordSummary() {

            let vm = this;

            axios.get('/catch_release/record/summary/' + vm.recordId)
                .then(response => {
                    if (response.data.response.error.error_code == 0) {
                        vm.summary.stage = response.data.response.data.record_details.stage;
                        vm.newStageSet = response.data.response.data.record_details.stage;

                        vm.summary.sterilizationStatus = response.data.response.data.record_details.sterilisation_status;

                        vm.summary.outcome = response.data.response.data.record_details.outcome;

                        if (vm.summary.outcome.toLowerCase() === 'still in care') {
                            vm.stillInCare = true;
                        } else {
                            vm.stillInCare = false;
                        }

                        vm.newOutcomeSet = response.data.response.data.record_details.outcome;

                        vm.summary.note = response.data.response.data.record_details.note;
                        vm.summary.species = response.data.response.data.record_details.animal.species;
                        vm.animalImages.push(response.data.response.data.record_details.animal.most_recent_image_url);
                        vm.animalImages.push(response.data.response.data.record_details.image_url);
                        vm.animalImageUrl = response.data.response.data.record_details.image_url;
                        vm.summary.gender = response.data.response.data.record_details.animal.gender;

                        vm.isFemaleRecord = vm.summary.gender.toLowerCase() == 'female';

                        vm.summary.hsappsId = response.data.response.data.record_details.animal.hsapps_id;
                        vm.summary.rfid = response.data.response.data.record_details.animal.rf_id;
                        vm.summary.collarId = response.data.response.data.record_details.collar_id;
                        vm.summary.animalName = response.data.response.data.record_details.animal.name;
                        vm.summary.colour = response.data.response.data.record_details.animal.colour;

                        vm.summary.newNote = response.data.response.data.record_details.note;
                        vm.summary.newTempCollarId = response.data.response.data.record_details.collar_id;
                        vm.summary.newColour = response.data.response.data.record_details.animal.colour;
                        vm.summary.newGender = response.data.response.data.record_details.animal.gender;
                        vm.summary.newAnimalName = response.data.response.data.record_details.animal.name;
                    }
                }).catch(e => {

            });

        },

        saveRecordSummaryDetails() {
            let vm = this;

            let newNote = vm.summary.newNote;
            let newCollarId = vm.summary.newTempCollarId;
            let newColour = vm.summary.newColour;
            let newGender = vm.summary.newGender;
            let newName = vm.summary.newAnimalName;

            axios.post('/catch_release/record/edit/summary/details/' + vm.recordId, {
                new_note: newNote,
                new_colour: newColour,
                new_gender: newGender,
                new_collar_id: newCollarId,
                new_animal_name: newName,
                species_name: vm.summary.species
            }).then(response => {
                if (response.data.response.error.error_code == 0) {
                    vm.summary.note = newNote
                    vm.summary.colour = newColour
                    vm.summary.gender = newGender
                    vm.summary.collarId = newCollarId
                    vm.summary.animalName = newName

                    vm.summary.newNote = "";
                    vm.summary.newTempCollarId = "";
                    vm.summary.newColour = "";
                    vm.summary.newGender = "";
                    vm.summary.newAnimalName = "";

                    vm.showAlert('Success', response.data.response.error.msg, true);
                }
            }).catch(e => {
                console.log(e);
            })

        },
        changeRecordStageAndOutcome() {
            let vm = this
            if (vm.newStageSet.toLowerCase() === 'released/out-of-care') {
                if (vm.newOutcomeSet.toLowerCase() === 'still in care') {
                    alert('Outcome must be set to still in care to set stages other than released/out-of-care')
                    return
                }
            }

            if (vm.newStageSet.toLowerCase() !== 'released/out-of-care') {
                if (vm.newOutcomeSet.toLowerCase() !== 'still in care') {
                    alert('Outcome must be set to still in care to set stages other than released/out-of-care')
                    return
                }
            }

            if (['Died Pre Op', 'Died Post Op'].includes(vm.newOutcomeSet)) {
                if (vm.death_cause == '') {
                    alert("Please set death cause")
                    return;
                }

                if (vm.death_cause == 'other' && vm.death_cause_other == '') {
                    alert("Please write other death cause")
                    return;
                }
            }

            vm.changeRecordOutcome()
            vm.changeRecordStage()
            vm.stageAndOutcomeChangeDialogVisible = false
        },

        showStageAndOutcomeChangeDialog() {
            this.stageAndOutcomeChangeDialogVisible = true
        }

    },
    computed: {
        getLocationLink() {
            return 'https://www.google.com/maps/search/?api=1&query=';
        }
    },
    mounted() {
        const vm = this
        vm.isReadOnly = false
        vm.$store.getters.getUserRoleForCurrentProject.forEach(r => {
            if (r.role_name == 'VIEWER') {
                vm.isReadOnly = true
            }
        })
        let project = vm.$store.getters.getCurrentSelectedProject;
        this.list.species = project.project_animals;
        this.$root.$on('UpdateSummaryComponent', () => {
            // your code goes here
            this.getRecordSummary()
        });
        this.getRecordSummary();
    }

}

</script>
<style scoped>
.summary-container {
    padding: 0;
    max-width: 100%;
}

.summary-heading {
    margin: 0 0 12px 0;
    font-size: 18px;
    font-weight: 600;
    color: #2c3e50;
}

/* Unified Card Styles */
.summary-card-unified {
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
    overflow: hidden;
    border: 1px solid #e8e8e8;
    margin-bottom: 16px;
}

.card-content-unified {
    display: grid;
    grid-template-columns: 240px 1fr;
    gap: 0;
}

@media (max-width: 992px) {
    .card-content-unified {
        grid-template-columns: 1fr;
    }
}

/* Image Section */
.image-section {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 16px;
    background: #fafafa;
    border-right: 1px solid #e8e8e8;
}

@media (max-width: 992px) {
    .image-section {
        border-right: none;
        border-bottom: 1px solid #e8e8e8;
        padding: 16px;
    }
}

.image-wrapper {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
}

.animal-image {
    width: 100%;
    max-width: 200px;
    height: 200px;
    border-radius: 8px;
    object-fit: cover;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    cursor: pointer;
    transition: transform 0.2s ease;
    margin: 0 auto;
}

.animal-image:hover {
    transform: scale(1.02);
}

.note-section-image {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.note-label-small {
    font-size: 11px;
    font-weight: 500;
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.note-text-small {
    margin: 0;
    padding: 8px 12px;
    background: #ffffff;
    border-radius: 6px;
    color: #495057;
    font-size: 13px;
    line-height: 1.5;
    border-left: 3px solid #66615B;
    word-break: break-word;
}

/* Details Section */
.details-section {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px;
}

.info-section {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.info-section:not(:last-child) {
    padding-bottom: 16px;
    border-bottom: 1px solid #f0f0f0;
}

/* Section Header with Button */
.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 4px;
}

.section-title {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    color: #2c3e50;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

/* Info Grid */
.info-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    row-gap: 14px;
}

@media (max-width: 768px) {
    .info-grid {
        grid-template-columns: 1fr;
    }
}

.info-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.info-label {
    font-size: 11px;
    font-weight: 500;
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.info-value {
    font-size: 14px;
    font-weight: 500;
    color: #2c3e50;
    word-break: break-word;
}

/* Status Section */
.status-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    row-gap: 14px;
}

@media (max-width: 768px) {
    .status-grid {
        grid-template-columns: 1fr;
    }
}

/* Note Text */
.note-text {
    margin: 0;
    padding: 8px 12px;
    background: #f8f9fa;
    border-radius: 6px;
    color: #495057;
    font-size: 13px;
    line-height: 1.5;
    border-left: 3px solid #66615B;
}

/* Modern Button Styles */
.modern-btn {
    padding: 8px 16px;
    font-size: 12px;
    font-weight: 500;
    text-transform: none;
    letter-spacing: 0.3px;
    border-radius: 4px;
    border: 1px solid #d0d0d0;
    cursor: pointer;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    box-shadow: none;
    background: #ffffff;
    color: #555;
}

.modern-btn:hover {
    background: #f5f5f5;
    border-color: #999;
    color: #333;
}

.modern-btn:active {
    background: #e8e8e8;
}

.modern-btn.btn-primary {
    background: #ffffff;
    color: #555;
    border-color: #d0d0d0;
}

.modern-btn.btn-primary:hover {
    background: #f5f5f5;
    border-color: #999;
    color: #333;
}

.modern-btn.btn-secondary {
    background: #1e88e5;
    color: #ffffff;
    border-color: #1e88e5;
}

.modern-btn.btn-secondary:hover {
    background: #1565c0;
    border-color: #1565c0;
    color: #ffffff;
}

.modern-btn.btn-small {
    padding: 6px 12px;
    font-size: 12px;
}

.btn-text {
    line-height: 1;
}

/* Edit Details Dialog */
.edit-dialog {
    display: flex;
    flex-direction: column;
    padding: 8px;
}

.dialog-header {
    padding: 0 0 12px 0;
    border-bottom: 1px solid #e8e8e8;
    margin-bottom: 16px;
}

.dialog-header h4 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #2c3e50;
}

.dialog-content {
    padding: 0;
}

.form-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
    row-gap: 14px;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.form-group-full {
    grid-column: 1 / -1;
}

.form-group label {
    font-size: 12px;
    font-weight: 500;
    color: #555;
    margin: 0;
}

.dialog-footer {
    display: flex;
    justify-content: flex-end;
    padding-top: 16px;
    padding-right: 8px;
    border-top: 1px solid #e8e8e8;
    margin-top: 16px;
}

.save-btn {
    padding: 8px 20px;
    font-size: 13px;
    font-weight: 500;
    background: #1e88e5;
    color: #ffffff;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: background 0.2s ease;
}

.save-btn:hover {
    background: #1565c0;
}

/* Legacy label support for dialogs */
label {
    margin-right: 10px;
    font-weight: 500;
    color: #495057;
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .details-section {
        padding: 12px;
        gap: 12px;
    }
    
    .section-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }
    
    .modern-btn.btn-small {
        width: 100%;
        justify-content: center;
    }
    
    .image-section {
        padding: 12px;
    }
    
    .animal-image {
        width: 100%;
        max-width: 160px;
        height: auto;
        aspect-ratio: 1;
    }
    
    .form-grid {
        grid-template-columns: 1fr;
        gap: 12px;
    }
}
</style>

<style>
/* Global styles for popover - not scoped */
.edit-detail-popover {
    padding: 16px !important;
    border-radius: 8px !important;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15) !important;
}

.edit-detail-popover .el-input__inner,
.edit-detail-popover .el-textarea__inner {
    border-radius: 4px;
    border-color: #d9d9d9;
    font-size: 13px;
}

.edit-detail-popover .el-input__inner:focus,
.edit-detail-popover .el-textarea__inner:focus {
    border-color: #1e88e5;
}

.edit-detail-popover .el-select {
    width: 100%;
}
</style>
